import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const QuotationList = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Format ID with leading zeros
  const formatQuotationId = (id) => {
    return `QTN-${String(id).padStart(3, '0')}`;
  };

  // Fetch quotations from API
  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post("/quotation_list.php", {});
      
      if (response.data.head.code === 200) {
        // Transform the data - generate formatted quotation ID
        const transformedData = response.data.body.map(quotation => ({
          id: quotation.id,
          quotationId: formatQuotationId(quotation.id), // Use formatted ID
          rawId: quotation.id, // Keep original ID for operations
          date: quotation.quotation_date,
          customerName: quotation.customer_name,
          customer_id: quotation.customer_id,
          sub_total: parseFloat(quotation.sub_total) || 0,
          gst_percentage: parseFloat(quotation.gst_percentage) || 0,
          gst_amount: parseFloat(quotation.gst_amount) || 0,
          grand_total: parseFloat(quotation.grand_total) || 0,
          status: quotation.status,
          created_at: quotation.created_at,
          // Generate sample phone number for now (you should get this from API)
          phoneNumber: "98" + Math.floor(10000000 + Math.random() * 90000000).toString(),
          amount: `₹${parseFloat(quotation.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        }));
        
        setQuotations(transformedData);
      } else if (response.data.head.code === 606) {
        setQuotations([]);
      } else {
        setError(response.data.head.msg || "Failed to fetch quotations");
      }
    } catch (err) {
      console.error("Error fetching quotations:", err);
      setError("Failed to load quotations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete quotation handler
  const handleDelete = async (id, quotationId) => {
    if (!window.confirm(`Are you sure you want to delete quotation ${quotationId}?`)) {
      return;
    }

    try {
      const response = await api.post("/quotation_delete.php", { id });
      
      if (response.data.head.code === 200) {
        setQuotations(quotations.filter((q) => q.id !== id));
        alert("Quotation deleted successfully!");
      } else {
        alert("Failed to delete quotation: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting quotation:", err);
      alert("Failed to delete quotation. Please try again.");
    }
  };

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "pending") return "status-badge status-pending";
    if (s === "accepted" || s === "approved" || s === "granted" || s === "paid")
      return "status-badge status-approved";
    if (s === "rejected" || s === "declined" || s === "overdue")
      return "status-badge status-rejected";
    if (s === "draft") return "status-badge status-default";
    return "status-badge status-default";
  };

  // Filter quotations based on search, date range, and status
  const filteredQuotations = quotations.filter((quotation) => {
    if (!search && !fromDate && !toDate && !statusFilter) return true;
    
    const query = search.toLowerCase();
    const formattedId = formatQuotationId(quotation.id).toLowerCase();
    const matchesSearch = 
      String(quotation.id).toLowerCase().includes(query) ||
      formattedId.includes(query) ||
      (quotation.customerName && quotation.customerName.toLowerCase().includes(query)) ||
      (quotation.phoneNumber && quotation.phoneNumber.includes(query)) ||
      (quotation.amount && quotation.amount.toLowerCase().includes(query));

    // Date range filtering
    const quotationDate = new Date(quotation.date);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const matchesDateRange = 
      (!from || quotationDate >= from) && 
      (!to || quotationDate <= to);

    // Status filtering
    const matchesStatus = !statusFilter || quotation.status === statusFilter;

    return matchesSearch && matchesDateRange && matchesStatus;
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentQuotations,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredQuotations, 10);

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearStatusFilter = () => {
    setStatusFilter("");
  };

  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
  };

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading quotations...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchQuotations}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Quotation Management</h2>
      
      {/* Filters Section */}
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          {/* Date Range Filters */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          
          {/* Status Filter */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filter Actions */}
          <Col xs={12} md={6} lg={3} className="d-flex align-items-end gap-2">
            <Button 
              variant="outline-secondary" 
              onClick={clearDateFilter}
              disabled={!fromDate && !toDate}
              size="sm"
            >
              Clear Dates
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={clearAllFilters}
              disabled={!fromDate && !toDate && !statusFilter && !search}
              size="sm"
            >
              Clear All
            </Button>
          </Col>
        </Row>
      </div>

      {/* Search and Add Quotation Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search by quotation ID (e.g., QTN-001), customer, or amount"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
          <Link
            to="/addquotation"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Quotation
          </Link>
        </Col>
      </Row>

      {/* Table Section */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Quotation ID</th>
                <th>Date</th>
                <th>Customer Name</th>
                <th>Mobile Number</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentQuotations.map((quotation, index) => (
                <tr key={quotation.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaFileAlt className="text-primary me-2" />
                      {quotation.quotationId}
                    </div>
                  </td>
                  <td>{quotation.date}</td>
                  <td>{quotation.customerName}</td>
                  <td>{quotation.phoneNumber}</td>
                  <td className="fw-bold right-align">{quotation.amount}</td>
                  <td>
                    <span className={getStatusClass(quotation.status)}>
                      {quotation.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-icons">
                      <Link
                        to={`/viewquotation/${quotation.id}`}
                        className="view-link"
                        title="View Quotation"
                      >
                        <FaEye className="view-icon" />
                      </Link>

                      <Link
                        to={`/editquotation/${quotation.id}`}
                        className="me-2"
                        title="Edit Quotation"
                      >
                        <FaEdit className="edit-icon" />
                      </Link>
                      <FaTrash
                        className="delete-icon"
                        title="Delete Quotation"
                        onClick={() => handleDelete(quotation.id, quotation.quotationId)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {currentQuotations.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <div className="text-muted">
                      <FaFileAlt size={48} className="mb-3" />
                      <p>No quotations found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Summary Footer */}
      <Row className="mt-4">
        <Col xs={12}>
          <div className="summary-footer p-3 border rounded bg-light">
            <Row>
              <Col md={4}>
                <strong>Total Quotations:</strong> {filteredQuotations.length}
              </Col>
              <Col md={4}>
                <strong>Total Amount:</strong> ₹{
                  filteredQuotations.reduce((total, quotation) => {
                    return total + (parseFloat(quotation.grand_total) || 0);
                  }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                }
              </Col>
              <Col md={4}>
                <strong>Filtered Results:</strong> {filteredQuotations.length} of {quotations.length}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default QuotationList;